/**
 * Résolution textStyle proposal → props web (rem, sans width).
 */

import { horizontalToTextAlign } from './proposalBoxAlign';
import { proposalPxToSpacingCss, resolveTextPaddingCss } from './proposalUnits';
import { formatFontSizeRem, parseFontSizeRem } from './proposalRichTextFonts';
import { computeTitleIndentPx } from './proposalTitleLayout';
import { siteTextStyles } from '../../shared/theme/themeTypography';
import { findConfigurationStyle } from '../../shared/boxes/styleTemplate';

function toColor(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.style || fallback;
}

/**
 * Trouve un textStyle dans la configuration ; défaut = textStyles[0].
 */
export function findConfigurationTextStyle(configuration, textStyleId) {
  const textStyles = configuration?.textStyles || [];
  return findConfigurationStyle(textStyles, textStyleId) || textStyles[0] || null;
}

/**
 * Interligne d'un texte proposal — **copie conforme** de
 * `calculateOptimalLineHeight` (`src/utils/styles.js`), et il faut que ça le
 * reste : c'est la même page rendue par deux applications.
 *
 * L'ancienne formule ici mettait la taille de police au numérateur
 * (`fontSize / 14 * 1.4`), héritée d'une époque où l'interligne était en px.
 * Sur un multiplicateur unitless, elle multipliait l'interligne par la taille :
 * un titre en 40 px sortait à `line-height: 4`, soit une ligne de 160 px — d'où
 * un fond de titre démesuré à côté du même titre dans le Proposal.
 */
function calculateOptimalLineHeight(fontSize, _baseFontSize = 14, baseLineHeight = 1.4, minLineHeight = 1.2) {
  if (!fontSize || fontSize <= 0) return baseLineHeight;
  return Math.max(minLineHeight, baseLineHeight);
}

function toCssPx(px, field, drawMode) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return '';
  return drawMode ? `${n}px` : proposalPxToSpacingCss(n);
}

function spacingCss(px, drawMode) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return drawMode ? '0' : '0';
  return drawMode ? `${n}px` : (proposalPxToSpacingCss(n) || '0');
}

/**
 * Reconstruit une configuration proposal minimale depuis projectConfig (éditeur Puck).
 */
export function configurationFromProjectConfig(projectConfig) {
  const ps = siteTextStyles(projectConfig);
  if (!ps) return null;

  const toTitleEntry = (entry) => {
    if (!entry) return null;
    return {
      fontSize: entry.fontSize ?? null,
      fontFamily: entry.fontFamily || '',
      fontWeight: entry.fontWeight || '',
      fontStyle: entry.fontStyle || '',
      textDecoration: entry.textDecoration || '',
      marginTop: entry.marginTop ?? 0,
      marginBottom: entry.marginBottom ?? 0,
      textAlign: entry.textAlign || null,
      color: entry.color ? { style: entry.color } : null,
      backgroundColor: entry.backgroundColor ? { style: entry.backgroundColor } : null,
    };
  };

  return {
    indentationTitles: projectConfig.indentationTitles ?? 0,
    fontFamily: ps.fontFamily || null,
    fontSize: ps.fontSize || null,
    baseFontColor: ps.baseFontColor ? { style: ps.baseFontColor } : null,
    title1: toTitleEntry(ps.title1),
    title2: toTitleEntry(ps.title2),
    title3: toTitleEntry(ps.title3),
    title4: toTitleEntry(ps.title4),
  };
}

/**
 * Style titre proposal (configuration.title1..4) — marges, typo.
 * Le décalage horizontal est géré par buildTitleIndentWrapperCss (largeur box legacy).
 * @param {number} titleLevel 1..4
 * @param {number[]|null} [indexTitle]
 */
export function buildWebTitleStyle(configuration, titleLevel, options = {}) {
  const { drawMode = false, indexTitle = null, indentFromLevelOnly = false } = options;
  const titleConfig = configuration?.[`title${titleLevel}`];
  if (!titleConfig || !configuration) return null;

  const marginTop = titleConfig.marginTop ?? 0;
  const marginBottom = titleConfig.marginBottom ?? 0;
  const indentPx = computeTitleIndentPx(configuration, titleLevel, indexTitle, {
    fromLevelOnly: indentFromLevelOnly,
  });
  const resolvedFontSize = titleConfig.fontSize || configuration.fontSize || null;
  const textAlign = titleConfig.textAlign
    ? horizontalToTextAlign(titleConfig.textAlign, 'left')
    : null;

  return {
    isProposalTitle: true,
    titleLevel,
    indentPx,
    fontFamily: titleConfig.fontFamily || configuration.fontFamily || null,
    fontSize: resolvedFontSize,
    color: toColor(titleConfig.color, toColor(configuration.baseFontColor)),
    backgroundColor: toColor(titleConfig.backgroundColor),
    fontWeight: titleConfig.fontWeight || null,
    fontStyle: titleConfig.fontStyle || null,
    textDecoration: titleConfig.textDecoration || null,
    textAlign,
    lineHeight: calculateOptimalLineHeight(resolvedFontSize),
    marginTop: spacingCss(marginTop, drawMode),
    marginBottom: spacingCss(marginBottom, drawMode),
    paddingTopBottom: 0,
    paddingLeftRight: 0,
  };
}

/**
 * @param {object} configuration
 * @param {string|number|null} textStyleId
 * @param {object} [box]
 * @param {{ drawMode?: boolean }} [options]
 */
export function buildWebTextStyle(configuration, textStyleId, box = null, options = {}) {
  const { drawMode = false } = options;
  const style = findConfigurationTextStyle(configuration, textStyleId);
  if (!style || !configuration) return null;

  const borderColor = toColor(style.border?.color);
  const borderSize = Number(style.border?.size || 0);
  const hasBorder = !!(borderSize > 0 && borderColor
    && style.border
    && (style.border.left || style.border.right || style.border.top || style.border.bottom));

  const resolvedFontSize = style.fontSize || configuration.fontSize || null;
  const horizontal = box?.positions?.horizontal ?? style.positions?.horizontal ?? 'left';
  const contentAlign = options.contentAlignment
    ?? box?.content?.alignment
    ?? horizontal;

  // Comme le classique (textTab) : le style Défaut (id 0) n'a pas de valeurs
  // propres, il lit celles du document. Rien n'est figé : le texte suit la
  // police, la taille et la couleur par défaut du site (variables CSS).
  const forced = style.id !== 0;
  return {
    fontFamily: (forced && style.fontFamily) || null,
    fontSize: (forced && style.fontSize) || null,
    color: forced ? toColor(style.color) : null,
    backgroundColor: toColor(style.backgroundColor),
    fontWeight: style.fontWeight || null,
    fontStyle: style.fontStyle || null,
    textDecoration: style.textDecoration || null,
    textAlign: horizontalToTextAlign(contentAlign, 'left'),
    lineHeight: calculateOptimalLineHeight(resolvedFontSize),
    paddingTopBottom: style.paddingTopBottom ?? 0,
    paddingLeftRight: style.paddinngLeftRight ?? 0,
    ...(drawMode
      ? {}
      : {
        paddingTop: toCssPx(style.paddingTopBottom ?? 0, 'paddingTopBottom', drawMode) || undefined,
        paddingBottom: toCssPx(style.paddingTopBottom ?? 0, 'paddingTopBottom', drawMode) || undefined,
        paddingLeft: toCssPx(style.paddinngLeftRight ?? 0, 'paddinngLeftRight', drawMode) || undefined,
        paddingRight: toCssPx(style.paddinngLeftRight ?? 0, 'paddinngLeftRight', drawMode) || undefined,
      }),
    borderRadius: style.border?.radius
      ? (drawMode ? style.border.radius : proposalPxToSpacingCss(style.border.radius))
      : undefined,
    borderColor: hasBorder ? borderColor : null,
    borderWidth: hasBorder
      ? (drawMode ? borderSize : proposalPxToSpacingCss(borderSize))
      : undefined,
    positions: style.positions || { horizontal: 'left', vertical: 'center' },
    listConfig: {
      customBulletPadding: configuration.customBulletPadding || '0px 0px 0px 0px',
      bulletValues: [
        configuration.unorderedListBulletValue0,
        configuration.unorderedListBulletValue1,
        configuration.unorderedListBulletValue2,
        configuration.unorderedListBulletValue3,
        configuration.unorderedListBulletValue4,
      ],
      bulletColors: [
        configuration.unorderedListBulletColor0?.style,
        configuration.unorderedListBulletColor1?.style,
        configuration.unorderedListBulletColor2?.style,
        configuration.unorderedListBulletColor3?.style,
        configuration.unorderedListBulletColor4?.style,
      ],
    },
  };
}

/** Styles inline pour ProposalRichText / éditeur. */
export function webTextStyleToInlineCss(textStyle, { drawMode = false } = {}) {
  if (!textStyle) return undefined;

  if (drawMode) {
    const pt = Number(textStyle.paddingTopBottom) || 0;
    const pl = Number(textStyle.paddingLeftRight) || 0;
    const padding = (pt > 0 || pl > 0)
      ? { padding: `${pt}px ${pl}px` }
      : {};
    const borderWidth = textStyle.borderWidth;
    const borderColor = textStyle.borderColor;
    const rawBorderWidth = typeof borderWidth === 'string'
      ? parseFloat(borderWidth)
      : borderWidth;

    return {
      ...(textStyle.fontFamily ? { fontFamily: `${textStyle.fontFamily}, sans-serif` } : {}),
      ...(textStyle.fontSize
        ? { fontSize: typeof textStyle.fontSize === 'number' ? `${textStyle.fontSize}px` : textStyle.fontSize }
        : {}),
      ...(textStyle.color ? { color: textStyle.color } : {}),
      ...(textStyle.backgroundColor && textStyle.backgroundColor !== 'transparent'
        ? { backgroundColor: textStyle.backgroundColor }
        : {}),
      ...(textStyle.fontWeight ? { fontWeight: textStyle.fontWeight } : {}),
      ...(textStyle.fontStyle ? { fontStyle: textStyle.fontStyle } : {}),
      ...(textStyle.textDecoration ? { textDecoration: textStyle.textDecoration } : {}),
      ...(textStyle.textAlign ? { textAlign: textStyle.textAlign } : {}),
      ...(textStyle.isProposalTitle
        ? {
          marginTop: textStyle.marginTop ?? 0,
          marginBottom: textStyle.marginBottom ?? 0,
        }
        : {}),
      ...padding,
      lineHeight: textStyle.lineHeight ?? 1.4,
      ...(rawBorderWidth > 0 && borderColor
        ? { border: `${rawBorderWidth}px solid ${borderColor}` }
        : {}),
      ...(textStyle.borderRadius
        ? { borderRadius: typeof textStyle.borderRadius === 'number'
          ? `${textStyle.borderRadius}px`
          : textStyle.borderRadius }
        : {}),
    };
  }

  const padding = resolveTextPaddingCss(textStyle);
  const borderWidth = textStyle.borderWidth;
  const borderColor = textStyle.borderColor;

  const titleMargins = textStyle.isProposalTitle
    ? {
      marginTop: textStyle.marginTop ?? 0,
      marginBottom: textStyle.marginBottom ?? 0,
    }
    : {};

  return {
    ...titleMargins,
    ...(textStyle.fontFamily ? { fontFamily: `${textStyle.fontFamily}, sans-serif` } : {}),
    ...(textStyle.fontSize
      ? { fontSize: formatFontSizeRem(parseFontSizeRem(textStyle.fontSize)) || undefined }
      : {}),
    ...(textStyle.color ? { color: textStyle.color } : {}),
    ...(textStyle.backgroundColor && textStyle.backgroundColor !== 'transparent'
      ? { backgroundColor: textStyle.backgroundColor }
      : {}),
    ...(textStyle.fontWeight ? { fontWeight: textStyle.fontWeight } : {}),
    ...(textStyle.fontStyle ? { fontStyle: textStyle.fontStyle } : {}),
    ...(textStyle.textDecoration ? { textDecoration: textStyle.textDecoration } : {}),
    ...(textStyle.textAlign ? { textAlign: textStyle.textAlign } : {}),
    ...padding,
    lineHeight: textStyle.lineHeight ?? 1.4,
    ...(borderWidth && borderColor
      ? { border: `${borderWidth} solid ${borderColor}` }
      : {}),
    ...(textStyle.borderRadius ? { borderRadius: textStyle.borderRadius } : {}),
    ...(textStyle.listConfig?.customBulletPadding
      ? { '--proposal-list-padding': textStyle.listConfig.customBulletPadding }
      : {}),
    ...(textStyle.listConfig?.bulletValues?.[0]
      ? { '--proposal-ul-bullet-0': textStyle.listConfig.bulletValues[0] }
      : {}),
    ...(textStyle.listConfig?.bulletValues?.[1]
      ? { '--proposal-ul-bullet-1': textStyle.listConfig.bulletValues[1] }
      : {}),
    ...(textStyle.listConfig?.bulletValues?.[2]
      ? { '--proposal-ul-bullet-2': textStyle.listConfig.bulletValues[2] }
      : {}),
    ...(textStyle.listConfig?.bulletValues?.[3]
      ? { '--proposal-ul-bullet-3': textStyle.listConfig.bulletValues[3] }
      : {}),
    ...(textStyle.listConfig?.bulletValues?.[4]
      ? { '--proposal-ul-bullet-4': textStyle.listConfig.bulletValues[4] }
      : {}),
    ...(textStyle.listConfig?.bulletColors?.[0]
      ? { '--proposal-ul-bullet-color-0': textStyle.listConfig.bulletColors[0] }
      : {}),
    ...(textStyle.listConfig?.bulletColors?.[1]
      ? { '--proposal-ul-bullet-color-1': textStyle.listConfig.bulletColors[1] }
      : {}),
    ...(textStyle.listConfig?.bulletColors?.[2]
      ? { '--proposal-ul-bullet-color-2': textStyle.listConfig.bulletColors[2] }
      : {}),
    ...(textStyle.listConfig?.bulletColors?.[3]
      ? { '--proposal-ul-bullet-color-3': textStyle.listConfig.bulletColors[3] }
      : {}),
    ...(textStyle.listConfig?.bulletColors?.[4]
      ? { '--proposal-ul-bullet-color-4': textStyle.listConfig.bulletColors[4] }
      : {}),
  };
}

/**
 * Le fond qu'un niveau de titre reçoit de la configuration du proposal.
 *
 * Il n'est ni dans les props du bloc ni dans son style : il est posé par une
 * variable CSS (`--proposal-hN-bg-color`, cf. `proposalStylesUtils`) sur le
 * `hN` lui-même. Un bloc fraîchement déposé l'affiche donc sans que rien, dans
 * ses réglages, ne puisse l'expliquer — d'où cette lecture partagée entre la
 * sidebar (pour l'afficher) et le rendu (pour le remplacer).
 */
export function proposalHeadingBackground(proposalStyles, level) {
  const entry = proposalStyles?.[`title${level}`];
  const color = entry?.backgroundColor;
  return color && color !== 'transparent' ? color : '';
}

/**
 * Style qui remplace le fond hérité d'un bloc de texte.
 *
 * La couleur choisie doit gagner sur la variable CSS, pas seulement peindre le
 * conteneur : sinon le titre garde son jaune par-dessus le fond choisi.
 */
export function headingBackgroundOverrideCss(color) {
  if (!color) return null;
  const value = color === 'transparent' ? 'transparent' : color;
  return {
    '--proposal-h1-bg-color': value,
    '--proposal-h2-bg-color': value,
    '--proposal-h3-bg-color': value,
    '--proposal-h4-bg-color': value,
  };
}
